/**
 * Wave 337 · Memorial flipbook.
 *
 * Between Specs and Credits the page slows down, the cinematic ends,
 * and the visitor is invited to *read* the project — not just look.
 * This scene is that pause: a flipbook of project_specs grouped by
 * namespace, one chapter per category (identidade, condomínio,
 * dimensões, níveis, alturas, esquadrias, estrutura, acabamentos,
 * fachada, cobertura, exterior, piscina, sistemas...).
 *
 * Desktop renders the spreads with `react-pageflip` (StPageFlip under
 * the hood — corner fold, flip animation, two-page spread). Mobile
 * (≤768 px or coarse pointer) gets a vertical card stack — the page-
 * turning animation reads worse on a 5″ screen and breaks scroll
 * inertia.
 *
 * SEO crawl: a hidden `.sr-only` mirror renders every page's text as
 * plain DOM so Google, OG scrapers, and screen readers see the full
 * memorial without needing JS to walk the StPageFlip canvas.
 *
 * Architecture · the page registry (PAGE_DEFS) is the single source of
 * truth. Each entry binds a namespace to a label, an intro line, and
 * the ordered spec keys to render. Adding a chapter means adding an
 * entry — never touching layout code.
 *
 * Resilience · `specValue` already returns a fallback when the key is
 * missing, so chapters with sparse data still render cleanly. Pages
 * that come back fully empty are dropped (we don't ship blank spreads
 * just because a namespace is registered).
 */

import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import HTMLFlipBook from 'react-pageflip';
import type { ProjectSpecs, PublicShowcase } from '../lib/api';

/**
 * Page registry · maps each spec namespace to a chapter.
 *
 * `keys` is ordered intentionally — it's how the data is presented in
 * the printed memorial. Don't sort alphabetically: the source memorial
 * has its own narrative order (identity → site → dimensions → heights
 * → finishes …) and we mirror it.
 */
type SpecKey = string;
interface PageDef {
  /** Namespace key in `project_specs` (and the slug for #anchor). */
  ns: string;
  /** Chapter title shown in the page header + index. */
  label: string;
  /** One-line subtitle under the title — sets context. */
  intro: string;
  /** Ordered spec keys to surface, with their human label. */
  rows: { key: SpecKey; label: string }[];
}

const PAGE_DEFS: PageDef[] = [
  {
    ns: 'identity',
    label: 'Identidade',
    intro: 'Quem assina, onde está, em que terreno.',
    rows: [
      { key: 'owner', label: 'Proprietário' },
      { key: 'arquitetura', label: 'Arquitetura' },
      { key: 'arquiteto_resp', label: 'Arquiteto responsável' },
      { key: 'engenheiro', label: 'Engenheiro' },
      { key: 'financeiro', label: 'Financeiro' },
      { key: 'loteamento', label: 'Loteamento' },
      { key: 'municipio', label: 'Município' },
      { key: 'quadra', label: 'Quadra' },
      { key: 'lotes', label: 'Lotes' },
      { key: 'rua', label: 'Endereço' },
    ],
  },
  {
    ns: 'condominium',
    label: 'Condomínio',
    intro: 'O que o loteamento entrega antes da casa.',
    rows: [
      { key: 'name', label: 'Nome' },
      { key: 'access', label: 'Acesso' },
      { key: 'gatehouse', label: 'Portaria' },
      { key: 'amenity_lagos', label: 'Clube' },
      { key: 'tapume_obra', label: 'Tapume de obra' },
      { key: 'obra_alarme', label: 'Alarme de obra' },
      { key: 'lixeira', label: 'Lixeira' },
      { key: 'abrigo_medidores', label: 'Abrigo de medidores' },
    ],
  },
  {
    ns: 'site',
    label: 'Terreno',
    intro: 'A topografia e os limites antes da implantação.',
    rows: [
      { key: 'lot_front', label: 'Frente do lote' },
      { key: 'lot_depth', label: 'Profundidade' },
      { key: 'lot_area', label: 'Área do lote' },
      { key: 'declividade', label: 'Declividade' },
      { key: 'recuo_frontal', label: 'Recuo frontal' },
      { key: 'recuo_lateral', label: 'Recuos laterais' },
      { key: 'recuo_fundos', label: 'Recuo de fundos' },
    ],
  },
  {
    ns: 'dimensions',
    label: 'Dimensões',
    intro: 'Áreas medidas a partir das máscaras de área e plantas A1.',
    rows: [
      { key: 'area_construida_total', label: 'Área construída total' },
      { key: 'area_corpo_principal', label: 'Corpo principal' },
      { key: 'area_garagem', label: 'Garagem' },
      { key: 'area_acesso_autos', label: 'Acesso de autos' },
      { key: 'area_demais_pavimentadas', label: 'Demais pavimentadas' },
      { key: 'area_piscina', label: 'Piscina + prainha' },
      { key: 'house_front_spans', label: 'Frente da casa' },
      { key: 'house_module_spans', label: 'Módulos internos' },
      { key: 'suite_module_spans', label: 'Módulo da suíte' },
      { key: 'pool_deck_module', label: 'Deck da piscina' },
      { key: 'ramp_slope_pct', label: 'Rampa de acesso' },
    ],
  },
  {
    ns: 'levels',
    label: 'Níveis',
    intro: 'Cotas de referência. O datum do piso principal organiza tudo.',
    rows: [
      { key: 'datum_main_floor', label: 'Piso principal (datum)' },
      { key: 'deck_high', label: 'Deck alto' },
      { key: 'deck_low', label: 'Deck baixo (solarium)' },
      { key: 'floor_garage', label: 'Garagem' },
      { key: 'floor_service', label: 'Área de serviço' },
      { key: 'pool_top', label: 'Borda da piscina' },
      { key: 'pool_bottom', label: 'Fundo da piscina' },
    ],
  },
  {
    ns: 'rooms',
    label: 'Cômodos',
    intro: 'Dimensões internas dos ambientes principais.',
    rows: [
      { key: 'estar_jantar', label: 'Estar / Jantar' },
      { key: 'cozinha', label: 'Cozinha' },
      { key: 'gourmet', label: 'Gourmet' },
      { key: 'lavanderia', label: 'Lavanderia' },
      { key: 'suite_master', label: 'Suíte master' },
      { key: 'suite_2', label: 'Suíte 2' },
      { key: 'suite_3', label: 'Suíte 3' },
      { key: 'suite_4', label: 'Suíte 4' },
      { key: 'banho_social', label: 'Banho social' },
      { key: 'lavabo', label: 'Lavabo' },
      { key: 'closet', label: 'Closet' },
      { key: 'circulacao', label: 'Circulação' },
    ],
  },
  {
    ns: 'openings',
    label: 'Esquadrias',
    intro: 'Quadro resumido das aberturas — portas, janelas, brises.',
    rows: [
      { key: 'pa1', label: 'PA1' },
      { key: 'pa2', label: 'PA2' },
      { key: 'pa5', label: 'PA5' },
      { key: 'pm1', label: 'PM1' },
      { key: 'pm2', label: 'PM2' },
      { key: 'j1', label: 'J1' },
      { key: 'j2', label: 'J2' },
      { key: 'j3', label: 'J3' },
      { key: 'brise_telado', label: 'Brise telado' },
    ],
  },
  {
    ns: 'heights',
    label: 'Alturas',
    intro: 'Pés-direitos, forros e elementos de fachada.',
    rows: [
      { key: 'social_pe_direito', label: 'Social — pé-direito' },
      { key: 'social_forro', label: 'Social — forro' },
      { key: 'circulacao_pe_direito', label: 'Circulação — pé-direito' },
      { key: 'circulacao_forro', label: 'Circulação — forro' },
      { key: 'garagem_pe_direito', label: 'Garagem' },
      { key: 'area_servico_pe_direito', label: 'Área de serviço' },
      { key: 'volume_caixa_dagua_acima', label: 'Caixa d\'água' },
      { key: 'duto_lareira_acima_teto', label: 'Duto da lareira' },
      { key: 'muro_h_max', label: 'Muros' },
      { key: 'cerca_viva_recuo_frontal', label: 'Cerca-viva (frontal)' },
      { key: 'cerca_viva_demais', label: 'Cerca-viva (demais)' },
    ],
  },
  {
    ns: 'structure',
    label: 'Estrutura',
    intro: 'Como a casa se sustenta.',
    rows: [
      { key: 'fundacao', label: 'Fundação' },
      { key: 'pilares', label: 'Pilares' },
      { key: 'lajes', label: 'Lajes' },
      { key: 'cobertura', label: 'Cobertura' },
      { key: 'estrutura_metalica', label: 'Estrutura metálica' },
    ],
  },
  {
    ns: 'finishes',
    label: 'Acabamentos',
    intro: 'Pisos, paredes e forros — o que o pé e a mão tocam.',
    rows: [
      { key: 'piso_social', label: 'Piso social' },
      { key: 'piso_suites', label: 'Piso suítes' },
      { key: 'piso_banhos', label: 'Piso banhos' },
      { key: 'piso_garagem', label: 'Piso garagem' },
      { key: 'piso_gourmet', label: 'Piso gourmet' },
      { key: 'piso_solarium', label: 'Piso solarium' },
      { key: 'piso_varanda', label: 'Piso varanda' },
      { key: 'piso_piscina', label: 'Piso piscina' },
      { key: 'paredes_secas', label: 'Paredes secas' },
      { key: 'paredes_molhadas', label: 'Paredes molhadas' },
      { key: 'tetos', label: 'Tetos / forros' },
    ],
  },
  {
    ns: 'facade',
    label: 'Fachada',
    intro: 'A linha de frente, painel a painel.',
    rows: [
      { key: 'panel_1_material', label: 'Painel 1 — material' },
      { key: 'panel_1_height', label: 'Painel 1 — altura' },
      { key: 'panel_2_material', label: 'Painel 2 — ripado' },
      { key: 'panel_3_material', label: 'Painel 3 — brise central' },
      { key: 'panel_3_palette', label: 'Painel 3 — paleta' },
      { key: 'panel_4_material', label: 'Painel 4 — pedras' },
      { key: 'estrutura_metalica', label: 'Estrutura metálica aparente' },
      { key: 'duto_lareira', label: 'Duto da lareira' },
    ],
  },
  {
    ns: 'roof',
    label: 'Cobertura',
    intro: 'A quinta fachada — vista de cima.',
    rows: [
      { key: 'tipo', label: 'Tipo' },
      { key: 'inclinacao', label: 'Inclinação' },
      { key: 'telha', label: 'Telha' },
      { key: 'calhas', label: 'Calhas' },
      { key: 'rufos', label: 'Rufos' },
      { key: 'beiral', label: 'Beiral' },
    ],
  },
  {
    ns: 'exterior',
    label: 'Exterior',
    intro: 'O que está entre a casa e o muro — paisagismo, deck, calçadas.',
    rows: [
      { key: 'deck_madeira', label: 'Deck' },
      { key: 'paisagismo', label: 'Paisagismo' },
      { key: 'pergolado', label: 'Pergolado' },
      { key: 'rampa_garagem', label: 'Rampa da garagem' },
      { key: 'passeio', label: 'Passeio' },
      { key: 'piscina_dim', label: 'Piscina' },
      { key: 'prainha_dim', label: 'Prainha' },
    ],
  },
  {
    ns: 'pool',
    label: 'Piscina',
    intro: 'Dimensões, profundidade, sistemas.',
    rows: [
      { key: 'dim', label: 'Dimensões' },
      { key: 'prof', label: 'Profundidade' },
      { key: 'volume', label: 'Volume' },
      { key: 'aquecimento', label: 'Aquecimento' },
      { key: 'tratamento', label: 'Tratamento' },
      { key: 'iluminacao', label: 'Iluminação' },
      { key: 'prainha', label: 'Prainha' },
    ],
  },
  {
    ns: 'systems',
    label: 'Sistemas',
    intro: 'Hidráulica, elétrica, automação.',
    rows: [
      { key: 'eletrica', label: 'Elétrica' },
      { key: 'hidraulica', label: 'Hidráulica' },
      { key: 'aquecimento_agua', label: 'Aquecimento de água' },
      { key: 'ar_condicionado', label: 'Ar condicionado' },
      { key: 'automacao', label: 'Automação' },
      { key: 'seguranca', label: 'Segurança' },
      { key: 'caixa_dagua', label: 'Caixa d\'água' },
    ],
  },
  {
    ns: 'art_contract',
    label: 'ART e Contratos',
    intro: 'O lado documental — quem assina o quê, com qual responsabilidade.',
    rows: [
      { key: 'art_arquitetura', label: 'ART arquitetura' },
      { key: 'art_estrutural', label: 'ART estrutural' },
      { key: 'art_eletrica', label: 'ART elétrica' },
      { key: 'art_hidraulica', label: 'ART hidráulica' },
      { key: 'art_execucao', label: 'ART execução' },
      { key: 'alvara', label: 'Alvará' },
      { key: 'habite_se', label: 'Habite-se' },
    ],
  },
];

interface MemorialFlipbookProps {
  data: PublicShowcase;
  specs: ProjectSpecs;
}

/**
 * Pre-filter the page registry against the actual specs payload.
 *
 * A namespace is "live" if at least ONE registered key returned a
 * value. Empty chapters get dropped so we never ship a spread that
 * just says "—" eight times.
 */
function buildLivePages(specs: ProjectSpecs): PageDef[] {
  return PAGE_DEFS.filter((def) => {
    const ns = specs[def.ns];
    if (!ns) return false;
    return def.rows.some((r) => ns[r.key]?.value);
  });
}

export function MemorialFlipbook({ data, specs }: MemorialFlipbookProps) {
  // Push the latest specs into the module-level cache so the cloned
  // pages StPageFlip mounts outside our React tree see the data. Done
  // synchronously during render — the pages are mounted next.
  setActiveSpecs(specs);
  const livePages = useMemo(() => buildLivePages(specs), [specs]);
  // Don't render a section heading + flipbook scaffold if the project
  // has no extracted specs yet — better to just skip than show an
  // empty book.
  if (livePages.length === 0) return null;

  const projectName =
    data.heroHeadline ?? data.slug.charAt(0).toUpperCase() + data.slug.slice(1);

  return (
    <section
      id="memorial"
      className="relative border-t border-line bg-bg py-20 sm:py-32"
      aria-label="Memorial descritivo"
    >
      <div className="mx-auto max-w-5xl px-6 sm:px-12">
        {/* Chapter eyebrow + headline. Stays understated — the book
            below is the protagonist. */}
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-[10px] font-medium uppercase tracking-[0.3em] text-accent">
            Memorial
          </p>
          <h2 className="mt-5 font-display text-4xl font-light leading-[1] tracking-tightest text-ink sm:text-5xl">
            O memorial descritivo.
          </h2>
          <p className="mx-auto mt-5 max-w-[44ch] text-sm leading-relaxed text-ink-muted sm:text-base">
            Não é um folder. É a documentação técnica do projeto, capítulo a
            capítulo — montada a partir do que foi extraído dos PDFs oficiais.
            Vire as páginas.
          </p>
        </div>

        {/* Desktop spread (≥ md). On wider screens it's a real two-page
            book; on tablet it gracefully collapses to single-page. */}
        <div className="mt-12 hidden md:block">
          <FlipbookDesktop projectName={projectName} pages={livePages} />
        </div>

        {/* Mobile fallback. The card-stack reads better on small
            screens than a miniature StPageFlip — page-turn gestures
            steal scroll inertia and the text becomes too small. */}
        <div className="mt-10 md:hidden">
          <FlipbookMobile projectName={projectName} pages={livePages} specs={specs} />
        </div>

        {/* Hidden mirror for crawlers / screen readers. Same content,
            no animation cost, fully indexable. */}
        <SeoMirror projectName={projectName} pages={livePages} specs={specs} />
      </div>
    </section>
  );
}

// ─────────────────────────────────────────── desktop flipbook

function FlipbookDesktop({
  projectName,
  pages,
}: {
  projectName: string;
  pages: PageDef[];
}) {
  // Two-tier ref: the React-side ref attached to <HTMLFlipBook>, plus
  // a separate ref to the StPageFlip *instance* itself (the object
  // returned by `bookRef.current.pageFlip()`). Wave 337 smoke showed
  // the React-side handle is sometimes flaky to read on click — the
  // ref callback below resolves the StPageFlip instance once on init
  // and we drive flips against that. More reliable across React.memo
  // and forwardRef wrappers.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const bookRef = useRef<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const pageFlipApi = useRef<any>(null);
  const [page, setPage] = useState(0);
  // Total = cover + index + each chapter + back cover. We need this
  // for the Anterior/Próxima disabled states + the page indicator.
  const totalPages = 2 + pages.length + 1;

  // onInit fires once StPageFlip finishes its first layout. Capturing
  // the instance here lets buttons/keyboard/etc. drive the book
  // without re-walking the React fiber on each click.
  const handleInit = useCallback(() => {
    if (bookRef.current?.pageFlip) {
      pageFlipApi.current = bookRef.current.pageFlip();
    }
  }, []);
  // Stable onFlip handler — react-pageflip stores the FIRST handler it
  // sees as a StPageFlip listener. If the prop is a fresh closure each
  // render, the listener never receives the new closure's setPage.
  const handleFlip = useCallback((e: { data: number }) => {
    setPage(e.data);
  }, []);

  const flipPrev = useCallback(() => {
    pageFlipApi.current?.flipPrev();
  }, []);
  const flipNext = useCallback(() => {
    pageFlipApi.current?.flipNext();
  }, []);

  return (
    <div className="relative">
      <BookShell>
        <HTMLFlipBook
          // Locked dimensions — StPageFlip needs concrete numbers to
          // compute fold geometry. We pick 480×620 because it gives
          // ~A5 proportions and centers comfortably on a desktop.
          width={480}
          height={620}
          minWidth={320}
          maxWidth={560}
          minHeight={420}
          maxHeight={720}
          size="stretch"
          maxShadowOpacity={0.5}
          showCover
          drawShadow
          mobileScrollSupport
          flippingTime={650}
          usePortrait={false}
          startPage={0}
          startZIndex={0}
          autoSize
          clickEventForward
          useMouseEvents
          swipeDistance={30}
          showPageCorners
          disableFlipByClick={false}
          className="memorial-book"
          style={{ background: 'transparent' }}
          ref={bookRef}
          onFlip={handleFlip}
          onInit={handleInit}
        >
          <CoverPage projectName={projectName} side="front" />
          <IndexPage pages={pages} />
          {pages.map((p) => (
            <ChapterPage key={p.ns} def={p} />
          ))}
          <CoverPage projectName={projectName} side="back" />
        </HTMLFlipBook>
      </BookShell>

      {/* Controls + page indicator. StPageFlip exposes flipNext /
          flipPrev / flip(n) on the ref. Using `as any` because the
          library's typings are loose. */}
      <div className="mt-6 flex items-center justify-center gap-6 text-[11px] uppercase tracking-[0.2em] text-ink-subtle">
        <button
          type="button"
          className="px-3 py-1.5 transition hover:text-ink disabled:opacity-30"
          onClick={flipPrev}
          disabled={page === 0}
          aria-label="Página anterior"
        >
          ← Anterior
        </button>
        <span className="tabular-nums text-ink-muted">
          {String(page + 1).padStart(2, '0')} / {String(totalPages).padStart(2, '0')}
        </span>
        <button
          type="button"
          className="px-3 py-1.5 transition hover:text-ink disabled:opacity-30"
          onClick={flipNext}
          disabled={page >= totalPages - 1}
          aria-label="Próxima página"
        >
          Próxima →
        </button>
      </div>
    </div>
  );
}

/**
 * Decorative shell behind the book — soft inset shadow that suggests
 * a desk surface without competing with the page. Kept very quiet:
 * the book itself carries the weight.
 */
function BookShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto flex w-full justify-center">
      <div
        className="relative"
        style={{
          // The flipbook component handles its own sizing; we just
          // give it room and a shadow base.
          filter: 'drop-shadow(0 30px 60px rgba(0,0,0,0.45))',
        }}
      >
        {children}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────── individual pages
//
// Every "page" passed to HTMLFlipBook must be a forwardRef-capable
// element so StPageFlip can attach geometry refs. Using a Page wrapper
// keeps the chrome (header bar, page number, footer rule) consistent.

interface PageWrapperProps {
  children: React.ReactNode;
  className?: string;
  number?: number;
  hard?: boolean;
}

const Page = forwardRef<HTMLDivElement, PageWrapperProps>(function Page(
  { children, className = '', number, hard },
  ref,
) {
  return (
    <div
      ref={ref}
      className={`memorial-page ${className}`}
      // `data-density="hard"` tells StPageFlip to render this as a
      // rigid cover-style page instead of a soft, foldable one.
      data-density={hard ? 'hard' : undefined}
      style={{
        background: '#0a0a0a',
        color: '#f5f5f4',
        boxSizing: 'border-box',
        padding: '40px 38px',
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 2,
        border: '1px solid rgba(255,255,255,0.06)',
      }}
    >
      {children}
      {typeof number === 'number' && (
        <span
          className="absolute bottom-5 right-6 text-[10px] tabular-nums tracking-[0.2em] text-ink-subtle"
          aria-hidden
        >
          {String(number).padStart(2, '0')}
        </span>
      )}
    </div>
  );
});

const CoverPage = forwardRef<
  HTMLDivElement,
  { projectName: string; side: 'front' | 'back' }
>(function CoverPage({ projectName, side }, ref) {
  if (side === 'front') {
    return (
      <Page ref={ref} hard>
        <div className="flex h-full flex-col justify-between">
          <p className="text-[10px] font-medium uppercase tracking-[0.35em] text-accent">
            Memorial Descritivo
          </p>
          <div>
            <h1 className="font-display text-4xl font-light leading-[0.95] tracking-tightest sm:text-5xl">
              {projectName}
            </h1>
            <p className="mt-4 max-w-[26ch] text-xs leading-relaxed text-ink-muted">
              Documentação técnica do projeto. Capítulos extraídos dos PDFs e
              memoriais oficiais.
            </p>
          </div>
          <div className="flex items-center justify-between text-[9px] uppercase tracking-[0.3em] text-ink-subtle">
            <span>Builder</span>
            <span>nullscience</span>
          </div>
        </div>
      </Page>
    );
  }
  return (
    <Page ref={ref} hard>
      <div className="flex h-full flex-col items-center justify-center text-center">
        <p className="text-[10px] font-medium uppercase tracking-[0.35em] text-accent">
          Fim
        </p>
        <p className="mt-4 max-w-[28ch] text-sm leading-relaxed text-ink-muted">
          Para o projeto completo — plantas, cortes, fichas técnicas — solicite
          o pacote integral abaixo.
        </p>
      </div>
    </Page>
  );
});

const IndexPage = forwardRef<HTMLDivElement, { pages: PageDef[] }>(function IndexPage(
  { pages },
  ref,
) {
  return (
    <Page ref={ref} number={1}>
      <p className="text-[10px] font-medium uppercase tracking-[0.3em] text-accent">
        Sumário
      </p>
      <h2 className="mt-3 font-display text-2xl font-light leading-tight tracking-tighter">
        Capítulos
      </h2>
      <ol className="mt-6 grid grid-cols-1 gap-y-2 text-[12px] leading-relaxed text-ink-muted sm:grid-cols-2 sm:gap-x-8">
        {pages.map((p, i) => (
          <li key={p.ns} className="flex items-baseline gap-3">
            <span className="tabular-nums text-ink-subtle">
              {String(i + 1).padStart(2, '0')}
            </span>
            <span className="text-ink">{p.label}</span>
          </li>
        ))}
      </ol>
    </Page>
  );
});

const ChapterPage = forwardRef<HTMLDivElement, { def: PageDef }>(function ChapterPage(
  { def },
  ref,
) {
  // We don't compute the visual page number on the chapter render
  // because StPageFlip controls the flipping order. Leaving `number`
  // out keeps the pagination clean — the bottom indicator handles the
  // running count.
  return (
    <Page ref={ref}>
      <header>
        <p className="text-[10px] font-medium uppercase tracking-[0.3em] text-accent">
          Capítulo
        </p>
        <h3 className="mt-2 font-display text-2xl font-light leading-tight tracking-tighter text-ink">
          {def.label}
        </h3>
        <p className="mt-2 max-w-[36ch] text-[11px] leading-relaxed text-ink-muted">
          {def.intro}
        </p>
      </header>
      <div className="mt-6 flex-1 overflow-hidden">
        <ChapterRows def={def} />
      </div>
    </Page>
  );
});

/**
 * Two-column key/value table. Pulls from the live specs at render
 * time via a `useSpecsContext` injection — but because that wires
 * into the flipbook awkwardly, we instead pass the specs through the
 * mobile path and rely on a render-time `useContext` here. To avoid
 * coupling, ChapterRows reads from a lightweight global injected at
 * MemorialFlipbook mount.
 */
function ChapterRows({ def }: { def: PageDef }) {
  // Read specs from the module-level cache filled by MemorialFlipbook
  // on every render — see `setActiveSpecs` below. This is intentional:
  // the flipbook library renders pages outside the React tree, so a
  // Context-based approach can't reach them. The cache is per-mount
  // and refreshed on every parent render.
  const specs = useActiveSpecs();
  const filled = def.rows
    .map((r) => ({ ...r, value: specs[def.ns]?.[r.key]?.value }))
    .filter((r) => !!r.value);

  if (filled.length === 0) {
    return (
      <p className="mt-4 text-[11px] italic text-ink-subtle">
        (Capítulo sem dados extraídos ainda.)
      </p>
    );
  }

  return (
    <dl className="space-y-3 text-[12px] leading-relaxed">
      {filled.map((row) => (
        <div key={row.key} className="border-b border-line/60 pb-2">
          <dt className="text-[10px] uppercase tracking-[0.18em] text-ink-subtle">
            {row.label}
          </dt>
          <dd className="mt-1 text-ink">{prettifyValue(row.value!)}</dd>
        </div>
      ))}
    </dl>
  );
}

// ─────────────────────────────────────────── module-level specs cache
//
// react-pageflip clones each page into its own DOM container outside
// the normal React reconciliation tree. Context can't cross that
// boundary, so we use a tiny module-level store + a lightweight
// subscriber hook so the cloned pages still get fresh specs on each
// MemorialFlipbook render.

let _activeSpecs: ProjectSpecs = {};
const _subscribers = new Set<() => void>();

function setActiveSpecs(specs: ProjectSpecs) {
  _activeSpecs = specs;
  _subscribers.forEach((cb) => cb());
}

function useActiveSpecs(): ProjectSpecs {
  const [, force] = useState(0);
  useEffect(() => {
    const cb = () => force((x) => x + 1);
    _subscribers.add(cb);
    return () => {
      _subscribers.delete(cb);
    };
  }, []);
  return _activeSpecs;
}

// ─────────────────────────────────────────── mobile fallback

function FlipbookMobile({
  projectName,
  pages,
  specs,
}: {
  projectName: string;
  pages: PageDef[];
  specs: ProjectSpecs;
}) {
  return (
    <div className="space-y-6">
      {/* Cover-equivalent card so the mobile reading order matches the
          desktop book. */}
      <article className="rounded-sm border border-line bg-bg-card p-6">
        <p className="text-[10px] font-medium uppercase tracking-[0.3em] text-accent">
          Memorial Descritivo
        </p>
        <h3 className="mt-3 font-display text-2xl font-light leading-tight tracking-tightest text-ink">
          {projectName}
        </h3>
        <p className="mt-3 text-[12px] leading-relaxed text-ink-muted">
          Documentação técnica do projeto. Capítulos extraídos dos PDFs e
          memoriais oficiais.
        </p>
      </article>

      {pages.map((def) => {
        const filled = def.rows
          .map((r) => ({ ...r, value: specs[def.ns]?.[r.key]?.value }))
          .filter((r) => !!r.value);
        if (filled.length === 0) return null;
        return (
          <article
            key={def.ns}
            className="rounded-sm border border-line bg-bg-card p-6"
          >
            <p className="text-[10px] font-medium uppercase tracking-[0.3em] text-accent">
              {def.label}
            </p>
            <p className="mt-2 text-[11px] leading-relaxed text-ink-muted">
              {def.intro}
            </p>
            <dl className="mt-5 space-y-3 text-[13px] leading-relaxed">
              {filled.map((row) => (
                <div key={row.key} className="border-b border-line/60 pb-2">
                  <dt className="text-[10px] uppercase tracking-[0.18em] text-ink-subtle">
                    {row.label}
                  </dt>
                  <dd className="mt-1 text-ink">{prettifyValue(row.value!)}</dd>
                </div>
              ))}
            </dl>
          </article>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────── SEO mirror

function SeoMirror({
  projectName,
  pages,
  specs,
}: {
  projectName: string;
  pages: PageDef[];
  specs: ProjectSpecs;
}) {
  return (
    <div className="sr-only" aria-hidden="false">
      <h2>Memorial descritivo — {projectName}</h2>
      {pages.map((def) => {
        const filled = def.rows
          .map((r) => ({ ...r, value: specs[def.ns]?.[r.key]?.value }))
          .filter((r) => !!r.value);
        if (filled.length === 0) return null;
        return (
          <section key={def.ns}>
            <h3>{def.label}</h3>
            <p>{def.intro}</p>
            <dl>
              {filled.map((row) => (
                <div key={row.key}>
                  <dt>{row.label}</dt>
                  <dd>{prettifyValue(row.value!)}</dd>
                </div>
              ))}
            </dl>
          </section>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────── value prettifier
//
// Some specs are stored as JSON arrays (`[6.10,1.20,12.35]`). When we
// surface them in the memorial we want a human-readable form, not the
// raw bracket notation. Strings stay as-is.

function prettifyValue(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed.startsWith('[') && !trimmed.startsWith('{')) return trimmed;
  try {
    const parsed = JSON.parse(trimmed);
    if (Array.isArray(parsed)) {
      // Numeric arrays → "6,10 + 1,20 + 12,35 m"
      if (parsed.every((v) => typeof v === 'number')) {
        return parsed.map((n) => Number(n).toLocaleString('pt-BR')).join(' + ');
      }
      // String arrays (palettes) → comma-joined
      if (parsed.every((v) => typeof v === 'string')) {
        return (parsed as string[]).join(' · ');
      }
    }
    return trimmed;
  } catch {
    return trimmed;
  }
}

// ─────────────────────────────────────────── specs sync (parent → cache)

/**
 * MemorialFlipbookSpecsBridge — small effect helper that keeps the
 * module-level specs cache in sync with whatever the parent passed
 * down. Exported as a hook used inside the public component.
 */
export function useMemorialSpecsSync(specs: ProjectSpecs): void {
  useEffect(() => {
    setActiveSpecs(specs);
  }, [specs]);
}
